import { useMemo } from "react";
import { useUser } from "@clerk/clerk-react";
import { useQuery } from "@tanstack/react-query";
import { SeasonStanding } from "../types/market";
import { useApiAuth } from "./useApiAuth";
import { apiUrl } from "../lib/api";
import { queryKeys } from "../lib/queryKeys";
import { canonicalRoundDisplayName } from "@sportex100/shared/utils/competitionIdentity.ts";

type UseMarketArgs = {
  competitionCode: string;
  seasonCode: string;
  poolCode: string;
  enabled?: boolean;
};

function useMarket(args: UseMarketArgs) {
  const isEnabled = args.enabled ?? true;
  const { acquireToken } = useApiAuth();
  const { isSignedIn: isAuthenticated } = useUser();

  const canLoad = Boolean(isEnabled && isAuthenticated && args.poolCode);
  const queryKey = useMemo(
    () => queryKeys.market(args.competitionCode, args.seasonCode, args.poolCode || "none"),
    [args.competitionCode, args.seasonCode, args.poolCode],
  );

  const query = useQuery({
    queryKey,
    enabled: canLoad,
    queryFn: async () => {
      const accessToken = await acquireToken();
      if (!accessToken) {
        throw new Error("Authentication required to fetch market.");
      }

      const url = apiUrl(
        `/api/competitions/${args.competitionCode}/seasons/${args.seasonCode}/market?pool_code=${args.poolCode}`,
      );
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch market (${response.status})`);
      }

      const payload: SeasonStanding[] = await response.json();
      return payload.map((team) => ({
        ...team,
        current_round_name:
          canonicalRoundDisplayName(team.current_round, team.current_round_name) ??
          team.current_round_name,
      }));
    },
  });

  return {
    standings: canLoad ? query.data ?? [] : [],
    isLoading: canLoad ? query.isLoading : false,
    isError: canLoad ? query.isError : false,
    reload: query.refetch,
  };
}

export default useMarket;
